import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';

const HE = 'https://datatracker.ietf.org/doc/html/rfc8305';
const RFC6724 = 'https://datatracker.ietf.org/doc/html/rfc6724';

/**
 * Negotiated application protocol (fingerprinting-techniques §8).
 *
 * The source technique is "a client remembers that IPv6 failed for a host and
 * goes straight to IPv4 on subsequent connections". That needs the ADDRESS
 * FAMILY of each connection, and a page cannot observe it: fetch has no
 * remote-address API, `nextHopProtocol` only reports the HTTP version, and
 * `h3` does not imply IPv6 (QUIC runs over IPv4 just as happily).
 *
 * An earlier revision also reported request timings and a "first request
 * slower" observation. Those were removed: they support no verdict, and a slow
 * first request is exactly what a cold DNS lookup or TLS handshake looks like.
 * What is left is the one genuinely useful fact a page can read — the protocol
 * family the connection negotiated (h3 vs h2/h1) — which is stable enough to
 * hash into the network part.
 */
export const dualStackProbe: Probe = {
  id: 'dualStack',
  label: 'Connection protocol',
  layer: 'network',
  tier: 'client',
  category: 'network',
  description: 'Negotiated application protocol family (no address-family verdict)',
  sources: [HE, RFC6724],
  budgetMs: 8000,

  async run(): Promise<ProbeResult> {
    // A same-origin resource with a unique query string per pass, so the
    // response itself is not cached while connection reuse still applies.
    const url = `/_headers?probe=protocol&t=${Date.now()}`;

    const protocols: string[] = [];
    let anyOk = false;

    for (let i = 0; i < 3; i++) {
      try {
        const res = await fetch(url, { cache: 'no-store' });
        await res.text();
        if (res.ok) anyOk = true;
      } catch {
        /* a failed sample is skipped */
      }
      // Resource-Timing entries can land a tick after fetch() resolves.
      await new Promise((r) => setTimeout(r, 30));
      const entry = performance
        .getEntriesByType('resource')
        .filter((e) => e.name.includes('probe=protocol'))
        .pop() as PerformanceResourceTiming | undefined;
      if (entry?.nextHopProtocol) protocols.push(entry.nextHopProtocol);
      await new Promise((r) => setTimeout(r, 60));
    }

    if (!anyOk) {
      return {
        signals: {
          available: na<boolean>('unavailable', 'client', 'probe requests failed'),
          exercised: ok(false, 'derived'),
        },
      };
    }

    const protocolSet = [...new Set(protocols)].sort();
    const sawH3 = protocolSet.some((p) => /h3|quic/i.test(p));
    const sawH2OrH1 = protocolSet.some((p) => /h2|http\/1\.1/i.test(p));
    const protocolFamily = sawH3 ? 'h3' : sawH2OrH1 ? 'h2-or-h1' : 'unknown';

    return {
      signals: {
        available: ok(true, 'client'),
        exercised: ok(true, 'derived'),
        protocols: ok(protocolSet, 'client'),
        sawQuic: ok(sawH3, 'derived'),
        protocolFamily: ok(protocolFamily, 'derived'),
        note: ok(
          'Reports only the negotiated application protocol. Page JS cannot observe whether a connection used IPv6 or IPv4, so no dual-stack preference verdict is drawn.',
          'client',
        ),
      },
      findings: [],
      // Coarse, stable bucket: the protocol family actually used.
      parts: {
        network: {
          protocolFamily,
        },
      },
    };
  },
};

registerProbe(dualStackProbe);
